import { vec2 } from "gl-matrix";

/** Space kept between two labels pushed apart so they don't overlap, in pixels. */
const LABEL_GAP = 2;

/**
 * How far two labels may overlap and still count as clear, in pixels. Positions are 32-bit, so a
 * label pushed to exactly touching another can round back onto it by a fraction of a pixel - and
 * without this, keep being found overlapping and pushed to the same spot.
 */
const OVERLAP_TOLERANCE = 0.01;

/**
 * Labels are kept even when their lines are too short to fit them, and the lines all meet at the
 * measured points, so on a short measurement the labels pile up on one another. Push each one down
 * until it clears the ones placed before it - the distance label first, so it never moves. Sizes
 * come from the rendered labels, since their width depends on the text.
 *
 * Always down, never towards whichever side is nearer: the nearer side flips as zooming moves the
 * labels past each other, which made them jump back and forth. Moving one way only also means a
 * label can never be pushed back onto one it already cleared, so it takes at most one push per
 * placed label to end up clear of all of them.
 */
export function separateLabels(labels: { element: HTMLElement | null; label: { position2d?: vec2 } | undefined }[]) {
    const placed: { center: vec2; halfWidth: number; halfHeight: number }[] = [];

    for (const { element, label } of labels) {
        const center = label?.position2d;
        if (!element || !center) {
            continue;
        }
        const halfWidth = element.offsetWidth / 2 + LABEL_GAP / 2;
        const halfHeight = element.offsetHeight / 2 + LABEL_GAP / 2;

        for (let pushes = 0; pushes <= placed.length; pushes++) {
            const other = placed.find(
                (p) =>
                    Math.abs(center[0] - p.center[0]) < halfWidth + p.halfWidth - OVERLAP_TOLERANCE &&
                    Math.abs(center[1] - p.center[1]) < halfHeight + p.halfHeight - OVERLAP_TOLERANCE,
            );
            if (!other) {
                break;
            }
            center[1] = other.center[1] + halfHeight + other.halfHeight;
        }

        placed.push({ center, halfWidth, halfHeight });
    }
}
